// src/utils/format.js
// 不動産（売買物件）表示用の整形ユーティリティ

/**
 * 販売価格の表示整形
 * - 数字のみ（カンマ・空白許容）の場合：円単位とみなしてカンマ区切り + 「円」（taxIncluded の時のみ「（税込）」付与）
 * - それ以外のテキスト（例：応相談）：そのまま表示
 * @param {string|number} raw
 * @param {boolean} [taxIncluded=true] 税込表示を付けるか（土地の非課税等で false）
 * @returns {string}
 */
export function formatPrice(raw, taxIncluded = true) {
  if (raw == null) return "";
  const s = String(raw).trim();
  if (!s) return "";
  // 半角数字・カンマ・空白のみを金額として扱う
  if (/^[0-9,\s]+$/.test(s)) {
    const num = Number(s.replace(/[,\s]/g, ""));
    if (!Number.isNaN(num)) {
      return `${num.toLocaleString("ja-JP")}円${taxIncluded ? "（税込）" : ""}`;
    }
  }
  return s;
}

/**
 * 面積の表示整形
 * - 数値部分を取り出し、「◯◯㎡（約△△坪）」形式で表示（1㎡ = 0.3025坪）
 * - 数値が取れない場合はそのまま表示
 * @param {string|number} raw
 * @returns {string}
 */
export function formatArea(raw) {
  if (raw == null) return "";
  const s = String(raw).trim();
  if (!s) return "";
  const num = parseFloat(s.replace(/[^0-9.]/g, ""));
  if (Number.isNaN(num)) return s;
  const tsubo = num * 0.3025;
  // 坪は小数第2位まで、末尾の余分な0は除去
  const tsuboStr = tsubo.toFixed(2).replace(/\.?0+$/, "");
  return `${num}㎡（約${tsuboStr}坪）`;
}

/**
 * 数値に単位を付けて表示（例：道路幅員 "4"→"4m"、建ぺい率 "60"→"60%"）。
 * - 数値が取れない場合はそのまま表示
 * @param {string|number} raw
 * @param {string} unit
 * @returns {string}
 */
export function formatUnit(raw, unit) {
  if (raw == null) return "";
  const s = String(raw).trim();
  if (!s) return "";
  const num = parseFloat(s.replace(/[^0-9.]/g, ""));
  if (Number.isNaN(num)) return s;
  return `${num}${unit}`;
}

/**
 * Googleマップ埋め込み用：値が <iframe ... src="URL" ...> の場合は src の URL だけを取り出す。
 * 既に URL だけの場合はそのまま返す。
 * @param {string} value
 * @returns {string}
 */
export function extractMapSrc(value) {
  if (value == null) return "";
  const s = String(value).trim();
  if (!s) return "";
  // iframe タグが含まれていれば src="..." / src='...' を抽出
  const m = s.match(/src\s*=\s*["']([^"']+)["']/i);
  if (m) return m[1];
  return s;
}

/**
 * 面積入力欄用：半角数字と小数点のみを許可するサニタイズ
 * @param {string} value
 * @returns {string}
 */
export function sanitizeAreaInput(value) {
  if (value == null) return "";
  // 半角数字と「.」以外を除去し、小数点は最初の1つだけ残す
  let v = String(value).replace(/[^0-9.]/g, "");
  const firstDot = v.indexOf(".");
  if (firstDot !== -1) {
    v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, "");
  }
  return v;
}
